import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  Grid,
  Button,
} from "@mui/material";
import {
  DirectionsBus,
  Security,
  Shield,
  Lock,
  VerifiedUser,
  Backup,
  Warning,
  Gavel,
  Update,
  ArrowBack,
  ContactMail,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import WhiteLogo from "../../assets/logos/gaadikharcha-logo-white.png";
import { useNavigate } from "react-router-dom";

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 2),
  backgroundColor: "#fff",
}));

const PolicySection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
}));

const SecurityPolicy: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1a365d",
          borderBottom: "1px solid #2d4a69",
          borderRadius: 0,
          zIndex: 1200,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "72px",
          }}
        >
          {/* Left side - GaadiKharcha.com with menu icon */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={WhiteLogo}
              alt="GaadiKharcha"
              style={{ width: "120px", height: "auto" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate("/")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
              }}
            >
              <ArrowBack />
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
          color: "#ffffff",
          py: { xs: 4, md: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: "20px 20px",
            opacity: 0.3,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Security
                sx={{
                  fontSize: 48,
                  color: "#60a5fa",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                }}
              />
            </Box>
            <Typography
              variant="h2"
              fontWeight={700}
              gutterBottom
              sx={{
                fontSize: { xs: "1.75rem", md: "2rem" },
                mb: 0.5,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Security Policy
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 400,
                mx: "auto",
                mb: 0.5,
                opacity: 0.9,
                lineHeight: 1.3,
                fontSize: "1rem",
              }}
            >
              Your data security is our top priority
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Introduction */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Shield sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      Introduction
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    Gaadikarcha.com: An Aequitous Platform
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    At Gaadikarcha.com, the safety and privacy of your business
                    data is our highest priority. We are committed to ensuring
                    your information remains secure, confidential, and protected
                    from misuse, loss, or unauthorized access.
                  </Typography>
                </PolicySection>

                {/* Data Protection Standards */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Lock sx={{ color: "#10b981", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      1. Data Protection Standards
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    We follow industry-standard security practices to safeguard
                    all user data:
                  </Typography>

                  <List>
                    {[
                      "Encrypted Data Storage: All data is stored in encrypted format using AES-256 encryption.",
                      "HTTPS (SSL) Everywhere: All communication between your device and our servers is protected with SSL (Secure Socket Layer) to prevent data interception.",
                      "Cloud Infrastructure: We host our application on secure, globally recognized cloud platforms with high availability and strong compliance standards.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Access Controls */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <VerifiedUser
                      sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      2. Access Controls
                    </Typography>
                  </Box>

                  <List>
                    {[
                      "Role-based access is implemented within the system to ensure only authorized users can access specific data.",
                      "Admin access is tightly controlled and logged.",
                      `Employees and contractors are granted access strictly on a "need-to-know" basis and are bound by confidentiality agreements.`,
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Application Security */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Security sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      3. Application Security
                    </Typography>
                  </Box>

                  <List>
                    {[
                      "We conduct regular security testing of the application, including vulnerability scanning and patching.",
                      "All user passwords are securely hashed before storage.",
                      "Session management, automatic logout, and CAPTCHA features help reduce risks of unauthorized access.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* User Responsibility */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Shield sx={{ color: "#ef4444", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      4. User Responsibility
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    You are responsible for:
                  </Typography>

                  <List>
                    {[
                      "Keeping your login credentials secure",
                      "Avoiding the use of weak or shared passwords",
                      "Reporting any suspicious activity immediately to our support team",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 2 }}
                  >
                    We recommend using strong passwords and updating them
                    periodically.
                  </Typography>
                </PolicySection>

                {/* Backup & Disaster Recovery */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Backup sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      5. Backup & Disaster Recovery
                    </Typography>
                  </Box>

                  <List>
                    {[
                      "Daily automated backups are maintained to prevent data loss.",
                      "Our recovery systems ensure platform availability and data integrity in case of outages or unexpected events.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Incident Response */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Warning sx={{ color: "#10b981", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      6. Incident Response
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    In case of any security breach or incident:
                  </Typography>

                  <List>
                    {[
                      "We have a documented Incident Response Plan in place.",
                      "Affected users will be notified promptly with full transparency.",
                      "We will take immediate corrective action to resolve and prevent future occurrences.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Compliance */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Gavel sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      7. Compliance
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    While not yet formally certified, we align our practices
                    with global standards such as:
                  </Typography>

                  <List>
                    {[
                      "ISO 27001 (Information Security Management)",
                      "SOC 2 (Service Organization Controls)",
                      "Indian IT Act for digital platforms",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Continuous Improvements */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Update sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      8. Continuous Improvements
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    Security is a continuous process. We:
                  </Typography>

                  <List>
                    {[
                      "Regularly update our systems and libraries",
                      "Monitor for vulnerabilities",
                      "Provide training to our team to stay ahead of threats",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Contact & Reporting */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ContactMail
                      sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      9. Contact & Reporting
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 3 }}
                  >
                    If you believe your data has been compromised or have found
                    a vulnerability in our system, please report it immediately:
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="#1a365d"
                      gutterBottom
                    >
                      Aequitous Services Pvt. Ltd. (formerly YSPL)
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Email:</strong> info@aequitous.com
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> +91 22 4515 2489
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      <strong>Address:</strong> Mumbai, Maharashtra, India
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 3 }}
                  >
                    We take all reports seriously and act quickly to resolve
                    issues.
                  </Typography>
                </PolicySection>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: "#0f172a",
          color: "#e2e8f0",
          py: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            © {new Date().getFullYear()} Aequitous Pvt. Ltd. All rights
            reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default SecurityPolicy;
